import { Component, DoCheck, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Order } from '@compumark/order-management-client';
import { isEqual, pickBy } from 'lodash';

import { OrdersApiClientService } from '../../../services/orders.api.client.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { diffObject } from '../../../shared/pipe/deep-diff-object.pipe';
import { ModelMapper } from '../../models/mapper/model-mapper';
import { UpdateOrderModel } from '../../models/update-order-model';
import { UpdateOrderService } from '../../services/update-order/update-order.service';

@Component({
  selector: 'app-update-order-dialog',
  templateUrl: './update-order-dialog.component.html',
  styleUrls: ['./update-order-dialog.component.scss'],
  standalone: false,
})
export class UpdateOrderDialogComponent implements DoCheck {
  data = inject<{
    model: UpdateOrderModel;
    focusTabIndex: number;
  }>(MAT_DIALOG_DATA);
  private snackbar = inject(SnackbarService);
  private ordersService = inject(OrdersApiClientService);
  dialogRef = inject<MatDialogRef<UpdateOrderDialogComponent>>(MatDialogRef);
  private updateOrderService = inject(UpdateOrderService);
  private modelMapper = inject(ModelMapper);

  private hideWatchedRegisterTab = [
    1560, 1561, 1567, 1562, 1563, 1568, 1566, 1564, 1570, 1533, 1535, 1536,
  ];
  public formValidation = true;
  public showWatchedRegisterTab = true;
  public showFullTextLookupTab = true;
  public saveButtonDisabled = false;
  public watchedRegisterTabDisabled = false;
  public referenceInfoTabDisabled = false;
  public fullTextLookupTabDisabled = false;
  private readonly update = 'update';
  private _order!: Order;
  focusTabIndex = 0;

  public get order(): Order {
    return this._order;
  }

  public set order(value: Order) {
    this._order = value;
    this.syncDisabledState();
  }

  ngDoCheck(): void {
    this.syncDisabledState();
  }

  constructor() {
    this.order = this.data.model.order!;
    this.focusTabIndex = this.data.focusTabIndex;
    this.syncDisabledState();
  }

  hideWatchedRegistersCheck(): boolean {
    return !this.hideWatchedRegisterTab.includes(this.order.productId!);
  }

  private syncDisabledState(): void {
    this.showWatchedRegisterTab = this.hideWatchedRegistersCheck();
    this.showFullTextLookupTab = this.hideFullTextLookupTab();
    this.saveButtonDisabled = this.isSaveBtnDisabled();
    this.watchedRegisterTabDisabled = !this.formValidation;
    this.referenceInfoTabDisabled = !this.formValidation;
    this.fullTextLookupTabDisabled = !this.formValidation;
  }

  public isSaveBtnDisabled(): boolean {
    return (
      !this.formValidation ||
      isEqual(
        pickBy(this.order, (value) => value !== null && value !== ''),
        pickBy(
          this.data.model.originalOrder,
          (value) => value !== null && value !== '',
        ),
      )
    );
  }

  public validateForm = (eventValue: Event) => {
    this.formValidation = !!eventValue;
    this.syncDisabledState();
  };

  updateOrder = () => {
    this.dialogRef.close();
    const snackBarRef = this.snackbar.openSnackBar(
      `Updates are now in progress for Watch Order "${this.order.trademarkName}".`,
      'Undo',
    );
    snackBarRef.onAction().subscribe(() => {
      this.updateOrderService.notifyUpdate(this.order.id!, 'open');
    });
    snackBarRef.afterDismissed().subscribe((dismissedRef) => {
      if (!dismissedRef.dismissedByAction) {
        const difference = diffObject(
          this.data.model.originalOrder!,
          this.order,
        );
        if (
          !Object.prototype.hasOwnProperty.call(difference, 'regions') &&
          (Object.prototype.hasOwnProperty.call(
            difference,
            'removedRegisters',
          ) ||
            Object.prototype.hasOwnProperty.call(difference, 'addedRegisters'))
        ) {
          difference.regions = this.order.regions;
        }
        difference.iSkip = this.order.iSkip;
        if (difference.assignee === 'unassigned') {
          difference.assignee = 'Unassigned';
        }
        this.ordersService
          .update(
            this.modelMapper.orderToUpdateOrder(difference, {
              action: 'UPDATE',
            }),
            this.order.id!,
          )
          .subscribe((response) => {
            if (response.ok) {
              this.updateOrderService.notifyUpdate(
                this.order.id!,
                this.update,
                response.body,
              );
            }
          });
      }
    });
  };

  cancel = () => {
    this.updateOrderService.notifyUpdate(this.order.id!, 'close');
  };

  // Temporary hiding full text lookup tab as part of TCM-121207.
  hideFullTextLookupTab = () => false;
}
